import { useEffect, useRef, useState } from 'react'
import { PRICE_BUCKET_COUNT, bucketHeightPct, isBucketDimmed, snapClamp } from './priceHistogram'

interface PriceHistogramSliderProps {
  min: number
  max: number
  step: number
  valueMin: number
  valueMax: number
  /** Bucket counts across [min, max]; empty renders the track with no bars. */
  buckets: number[]
  /** Fires continuously while dragging — drives the visual only. */
  onChange: (lo: number, hi: number) => void
  /** Fires once the interaction settles (pointer up, Enter/blur, key nudge). */
  onCommit: (lo: number, hi: number) => void
}

/**
 * Dual-thumb price slider with the accent histogram behind the track (#870
 * slice 5, toolkit: `searchbar-pro.js:1740-1935`). Bars outside the selected
 * range dim, the thumbs snap to `step`, and the min/max pills accept typed
 * values.
 *
 * Live-apply split (deliberate, toolkit's own reason): the reference routes
 * every drag frame through a 300ms-debounced refine. Here the visual updates
 * on every pointer move but the FILTER only commits when the interaction ends,
 * so a drag costs one refetch instead of a debounce timer that can be stranded
 * by the popover unmounting mid-drag.
 */
export default function PriceHistogramSlider({
  min, max, step, valueMin, valueMax, buckets, onChange, onCommit,
}: PriceHistogramSliderProps) {
  const trackRef = useRef<HTMLDivElement>(null)
  const [dragging, setDragging] = useState<'min' | 'max' | null>(null)
  // Live values during a drag, so onCommit gets the final position rather than
  // the last prop the parent happened to have flushed. The drag handler owns
  // this ref while a drag is in flight; the effect only re-seeds it from props.
  const liveRef = useRef({ lo: valueMin, hi: valueMax })
  useEffect(() => { liveRef.current = { lo: valueMin, hi: valueMax } }, [valueMin, valueMax])
  // The document-level drag listeners are installed once per drag, so they must
  // read the CURRENT callbacks: the price bounds behind onCommit can change
  // mid-drag when the meta range lands after first paint.
  const handlersRef = useRef({ onChange, onCommit })
  useEffect(() => { handlersRef.current = { onChange, onCommit } }, [onChange, onCommit])

  const bucketMax = buckets.length > 0 ? Math.max(...buckets) : 0
  const pct = (v: number) => (max <= min ? 0 : ((v - min) / (max - min)) * 100)

  // Typed pills mirror the slider unless the visitor is mid-edit.
  const [minText, setMinText] = useState(String(valueMin))
  const [maxText, setMaxText] = useState(String(valueMax))
  const editingRef = useRef<'min' | 'max' | null>(null)
  useEffect(() => {
    if (editingRef.current !== 'min') setMinText(String(valueMin))
    if (editingRef.current !== 'max') setMaxText(valueMax >= max ? `${valueMax}+` : String(valueMax))
  }, [valueMin, valueMax, max])

  const valueFromClientX = (clientX: number) => {
    const track = trackRef.current
    if (!track) return min
    const rect = track.getBoundingClientRect()
    const ratio = rect.width > 0 ? Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)) : 0
    return snapClamp(min + ratio * (max - min), step, min, max)
  }

  useEffect(() => {
    if (!dragging) return
    const move = (e: PointerEvent) => {
      const raw = valueFromClientX(e.clientX)
      const { lo, hi } = liveRef.current
      // The thumbs never cross: each is capped one step short of the other.
      const next = dragging === 'min'
        ? { lo: Math.min(raw, hi - step), hi }
        : { lo, hi: Math.max(raw, lo + step) }
      liveRef.current = next
      handlersRef.current.onChange(next.lo, next.hi)
    }
    const up = () => {
      setDragging(null)
      handlersRef.current.onCommit(liveRef.current.lo, liveRef.current.hi)
    }
    document.addEventListener('pointermove', move)
    document.addEventListener('pointerup', up)
    document.addEventListener('pointercancel', up)
    return () => {
      document.removeEventListener('pointermove', move)
      document.removeEventListener('pointerup', up)
      document.removeEventListener('pointercancel', up)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging, min, max, step])

  const nudge = (which: 'min' | 'max') => (e: React.KeyboardEvent) => {
    let delta = 0
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') delta = -step
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') delta = step
    if (e.key === 'PageDown') delta = -step * 10
    if (e.key === 'PageUp') delta = step * 10
    if (e.key === 'Home') delta = -(max - min)
    if (e.key === 'End') delta = max - min
    if (delta === 0) return
    e.preventDefault()
    // The non-crossing bound is itself clamped into [min, max] before it is
    // handed to snapClamp: when the whole catalog fits inside one step
    // (`max - min < step`, e.g. a single property or a flat rate),
    // `valueMax - step` sits BELOW min and `valueMin + step` sits ABOVE max,
    // and snapClamp — which checks `lo` first and returns it without
    // re-checking `hi` — would then emit that impossible bound as the value.
    // Reproduced: priceRange {min:500,max:502} with step 5, one ArrowLeft on
    // the max thumb reported aria-valuenow="505" against aria-valuemax="502".
    const next = which === 'min'
      ? { lo: snapClamp(valueMin + delta, step, min, Math.max(min, valueMax - step)), hi: valueMax }
      : { lo: valueMin, hi: snapClamp(valueMax + delta, step, Math.min(max, valueMin + step), max) }
    onChange(next.lo, next.hi)
    onCommit(next.lo, next.hi)
  }

  const commitTyped = (which: 'min' | 'max') => () => {
    editingRef.current = null
    const text = which === 'min' ? minText : maxText
    // Keep the decimal separator: stripping every non-digit turns "200.50"
    // into "20050", which then clamps to the top of the range — the visitor
    // types two hundred and gets no filter at all. parseFloat stops at a
    // second dot ("1.2.3" → 1.2) instead of returning NaN, and still returns
    // NaN for "" and ".", which the guard below catches. Nothing fractional
    // escapes: snapClamp rounds to the integer step.
    const parsed = parseFloat(text.replace(/[^0-9.]/g, ''))
    if (Number.isNaN(parsed)) {
      setMinText(String(valueMin))
      setMaxText(valueMax >= max ? `${valueMax}+` : String(valueMax))
      return
    }
    // Same sub-step guard as `nudge` above: keep the non-crossing bound inside
    // [min, max] so snapClamp can never be handed lo > hi.
    const next = which === 'min'
      ? { lo: snapClamp(parsed, step, min, Math.max(min, valueMax - step)), hi: valueMax }
      : { lo: valueMin, hi: snapClamp(parsed, step, Math.min(max, valueMin + step), max) }
    onChange(next.lo, next.hi)
    onCommit(next.lo, next.hi)
    // Repaint the pills from the COMMITTED value, unconditionally. The
    // props-keyed effect above only fires when the value actually changes, so
    // a clamp that lands back on the current prop (type 99999 into Maximum at
    // full range) would otherwise strand the typed text in the box while the
    // real bound is something else entirely.
    setMinText(String(next.lo))
    setMaxText(next.hi >= max ? `${next.hi}+` : String(next.hi))
  }

  return (
    <div className="hfse-sbp-price">
      <div className="hfse-sbp-price-histogram" data-bucket-count={buckets.length || PRICE_BUCKET_COUNT}>
        {buckets.map((count, i) => (
          <span
            key={i}
            className={`hfse-sbp-price-bar${isBucketDimmed(i, buckets.length, min, max, valueMin, valueMax) ? ' hfse-sbp-price-bar-dim' : ''}`}
            style={{ height: `${bucketHeightPct(count, bucketMax)}%` }}
            aria-hidden
          />
        ))}
      </div>

      <div className="hfse-sbp-price-track" ref={trackRef}>
        <div className="hfse-sbp-price-track-base" />
        <div
          className="hfse-sbp-price-track-fill"
          style={{ left: `${pct(valueMin)}%`, right: `${100 - pct(valueMax)}%` }}
        />
        <button
          type="button"
          className={`hfse-sbp-price-thumb hfse-sbp-price-thumb-min${dragging === 'min' ? ' hfse-sbp-price-thumb-dragging' : ''}`}
          style={{ left: `${pct(valueMin)}%` }}
          role="slider"
          aria-label="Minimum price"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={valueMin}
          onPointerDown={(e) => { e.preventDefault(); setDragging('min') }}
          onKeyDown={nudge('min')}
        />
        <button
          type="button"
          className={`hfse-sbp-price-thumb hfse-sbp-price-thumb-max${dragging === 'max' ? ' hfse-sbp-price-thumb-dragging' : ''}`}
          style={{ left: `${pct(valueMax)}%` }}
          role="slider"
          aria-label="Maximum price"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={valueMax}
          onPointerDown={(e) => { e.preventDefault(); setDragging('max') }}
          onKeyDown={nudge('max')}
        />
      </div>

      <div className="hfse-sbp-price-fields">
        <label className="hfse-sbp-price-field">
          <span className="hfse-sbp-price-field-label">Minimum</span>
          <span className="hfse-sbp-price-field-input">
            <span className="hfse-sbp-price-field-currency">$</span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              aria-label="Minimum price"
              value={minText}
              onFocus={() => { editingRef.current = 'min' }}
              onChange={(e) => setMinText(e.target.value)}
              onBlur={commitTyped('min')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur() }
                // Escape ONLY. commitTyped hangs off onBlur, and Radix's
                // DismissableLayer unmounts the content on Escape without a
                // blur ever reaching this input — the typed number just
                // vanished, against WHAT's own "nothing is silently
                // discarded" contract. Blur fires synchronously, so
                // commitTyped runs BEFORE the dismiss; no preventDefault and
                // no stopPropagation, so Radix still closes the popover. A
                // second commitTyped from the unmount is harmless: editingRef
                // is already nulled. (An outside click needs nothing extra —
                // a real pointerdown moves focus and does fire blur.)
                if (e.key === 'Escape') (e.target as HTMLInputElement).blur()
              }}
            />
          </span>
        </label>
        <label className="hfse-sbp-price-field">
          <span className="hfse-sbp-price-field-label">Maximum</span>
          <span className="hfse-sbp-price-field-input">
            <span className="hfse-sbp-price-field-currency">$</span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              aria-label="Maximum price"
              value={maxText}
              onFocus={() => { editingRef.current = 'max' }}
              onChange={(e) => setMaxText(e.target.value)}
              onBlur={commitTyped('max')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur() }
                // See the Minimum field above: Escape commits through blur
                // before Radix dismisses, and stays un-prevented so it closes.
                if (e.key === 'Escape') (e.target as HTMLInputElement).blur()
              }}
            />
          </span>
        </label>
      </div>
    </div>
  )
}
